/**
 * Drop image / video files onto the editor (same cyan drop lines as block drags).
 * Inserts local media blocks at the drop target and kicks off uploads.
 */

import { useEffect } from 'react';
import { stripHtml } from '../utils/caretUtils';
import {
  createBlock,
  findBlock,
  updateBlockInTree,
  insertBlock,
  insertBlockAfter,
} from '../utils/blockUtils';
import {
  createLocalImageBlock,
  imageFilesFromDataTransfer,
} from '../utils/imageFiles';
import {
  createLocalVideoBlock,
  videoFilesFromDataTransfer,
} from '../utils/videoFiles';
import { startDocImageUpload } from '../utils/docImageUploadBridge';
import { startDocVideoUpload } from '../utils/docVideoUploadBridge';

export function useMediaFileDrop(
  editorRootRef,
  blocksRef,
  commit,
  setSelectedBlockId,
  { updateFileDropTarget, clearFileDropTarget, peekDropTarget }
) {
  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return undefined;

    const startMediaUpload = (blockId, file, kind) => {
      if (kind === 'video') startDocVideoUpload(blockId, file);
      else startDocImageUpload(blockId, file);
    };

    const startUploadsForInserted = (mediaItems, insertedIds) => {
      mediaItems.forEach((item, i) => {
        const id = insertedIds[i];
        if (id && item.block.props?.uploadStatus === 'local') {
          startMediaUpload(id, item.file, item.kind);
        }
      });
    };

    const onDragOver = (e) => {
      if (![...(e.dataTransfer?.types || [])].includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      updateFileDropTarget(e.clientX, e.clientY);
    };

    const onDragLeave = (e) => {
      if (![...(e.dataTransfer?.types || [])].includes('Files')) return;
      // Leaving the editor (not just crossing into a child) clears the line
      if (e.relatedTarget && root.contains(e.relatedTarget)) return;
      clearFileDropTarget();
    };

    const onDrop = (e) => {
      const imageFiles = imageFilesFromDataTransfer(e.dataTransfer);
      const videoFiles = videoFilesFromDataTransfer(e.dataTransfer);
      const finalTarget = peekDropTarget();
      clearFileDropTarget();
      if (!imageFiles.length && !videoFiles.length) return;
      e.preventDefault();
      e.stopPropagation();

      const mediaItems = [
        ...imageFiles.map((file) => ({
          file,
          block: createLocalImageBlock(file),
          kind: 'image',
        })),
        ...videoFiles.map((file) => ({
          file,
          block: createLocalVideoBlock(file),
          kind: 'video',
        })),
      ];
      const mediaBlocks = mediaItems.map((m) => m.block);
      const newBlocks = [...mediaBlocks, createBlock('text')];
      const [first, ...rest] = newBlocks;

      const current = blocksRef.current;
      const targetId = finalTarget?.targetId;
      const zone = finalTarget?.zone;
      const target =
        targetId && findBlock(current, targetId) ? findBlock(current, targetId) : null;

      let next = current;
      let uploadIds = [];

      if (
        target &&
        target.type === 'text' &&
        stripHtml(target.content || '').trim() === '' &&
        (zone === 'top' || zone === 'bottom')
      ) {
        // Drop onto an empty paragraph → replace it (Notion-style)
        next = updateBlockInTree(current, targetId, {
          type: first.type,
          content: first.content,
          props: first.props,
          children: first.children,
        });
        let afterId = targetId;
        rest.forEach((b) => {
          next = insertBlockAfter(next, afterId, b);
          afterId = b.id;
        });
        // First media reuses the empty text's id; remaining media keep theirs
        uploadIds = [targetId, ...mediaBlocks.slice(1).map((b) => b.id)];
      } else if (target && zone) {
        next = insertBlock(current, first, targetId, zone);
        let afterId = first.id;
        rest.forEach((b) => {
          next = insertBlockAfter(next, afterId, b);
          afterId = b.id;
        });
        uploadIds = mediaBlocks.map((b) => b.id);
      } else {
        next = [...current, ...newBlocks];
        uploadIds = mediaBlocks.map((b) => b.id);
      }

      commit(next);
      setSelectedBlockId(null);
      startUploadsForInserted(mediaItems, uploadIds);
    };

    const onDragEnd = () => clearFileDropTarget();

    root.addEventListener('dragover', onDragOver);
    root.addEventListener('dragleave', onDragLeave);
    root.addEventListener('drop', onDrop);
    window.addEventListener('dragend', onDragEnd);
    return () => {
      root.removeEventListener('dragover', onDragOver);
      root.removeEventListener('dragleave', onDragLeave);
      root.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', onDragEnd);
      clearFileDropTarget();
    };
  }, [
    blocksRef,
    clearFileDropTarget,
    commit,
    editorRootRef,
    peekDropTarget,
    setSelectedBlockId,
    updateFileDropTarget,
  ]);
}
